import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MessageHandlerService } from '../services/message-handler.service';
import { SKIP_ERROR_TOAST } from './skip-error-toast.token';

/**
 * Shows a generic error toast for failed API calls. Screens that already
 * render a dedicated error state (e.g. OrgsService/ConfigService consumers)
 * should opt out per-request with `skipErrorToast()` to avoid double messaging;
 * screens with contextual error copy (e.g. change-password's mapError) should
 * opt out too and call `messageHandler.error(...)` themselves instead.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiUrl) || req.context.get(SKIP_ERROR_TOAST)) {
    return next(req);
  }

  const messageHandler = inject(MessageHandlerService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        messageHandler.error(genericErrorMessage(error));
      }
      return throwError(() => error);
    })
  );
};

function genericErrorMessage(error: HttpErrorResponse): string {
  if (error.status === 0) return 'Could not connect to the server.';
  if (error.status === 403) return 'You do not have permission to perform this action.';
  if (error.status === 404) return 'The requested resource was not found.';
  if (error.status >= 500) return 'A server error occurred. Please try again later.';
  return 'An unexpected error occurred.';
}
